import React, { useState, useEffect, useCallback } from 'react';
import useAuthBlob from '../hooks/useAuthBlob';
import { LightboxPortal, isImage, isVideo } from './FileAttachment';

function dateGroupLabel(dateStr) {
  const date = new Date(dateStr);
  const now = new Date();
  const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const diffDays = Math.round((startOfDay(now) - startOfDay(date)) / 86400000);

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (date.getFullYear() === now.getFullYear()) {
    return date.toLocaleDateString(undefined, { month: 'long' });
  }
  return date.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
}

function groupByDate(items) {
  const groups = [];
  let current = null;
  for (const item of items) {
    const label = dateGroupLabel(item.created_at);
    if (!current || current.label !== label) {
      current = { label, items: [] };
      groups.push(current);
    }
    current.items.push(item);
  }
  return groups;
}

function MediaTile({ item, apiUrl, token, onToggleFavorite, onOpen, onBlobReady }) {
  const [loaded, setLoaded] = useState(false);
  const isMedia = isImage(item.file_mime) || isVideo(item.file_mime);
  const fullUrl = item.file_url?.startsWith('http')
    ? item.file_url
    : `${apiUrl?.replace('/api', '')}${item.file_url}`;
  const { blobUrl, error } = useAuthBlob(fullUrl, token, isMedia);

  // Reports the fetched blob up to the gallery so opening the lightbox reuses it
  // instead of downloading the same full-resolution file a second time.
  useEffect(() => {
    if (blobUrl) onBlobReady(item.id, blobUrl);
  }, [blobUrl, item.id, onBlobReady]);

  return (
    <div className={`group relative aspect-square overflow-hidden rounded-lg bg-white/5
      ${item.favorited ? 'ring-2 ring-amber-400' : ''}`}>
      <button
        onClick={(e) => { e.stopPropagation(); onToggleFavorite(item); }}
        className={`absolute right-1.5 top-1.5 z-10 rounded-full p-1.5 backdrop-blur transition
          ${item.favorited ? 'bg-amber-400 text-black opacity-100' : 'bg-black/40 text-white/80 opacity-0 group-hover:opacity-100'}`}
        title={item.favorited ? 'Unfavorite' : 'Favorite'}
      >
        <svg className="h-3.5 w-3.5" fill={item.favorited ? 'currentColor' : 'none'} viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z" />
        </svg>
      </button>

      {item.deleted && (
        <span className="absolute left-1.5 top-1.5 z-10 rounded bg-red-500/80 px-1.5 py-0.5 text-[10px] font-medium text-white">
          deleted
        </span>
      )}

      {isMedia ? (
        error ? (
          <div className="flex h-full items-center justify-center text-xs text-slate-500">unavailable</div>
        ) : blobUrl ? (
          isVideo(item.file_mime) ? (
            <video
              src={blobUrl}
              className={`h-full w-full cursor-zoom-in object-cover transition-opacity duration-300 ${loaded ? 'opacity-100' : 'opacity-0'}`}
              muted
              onLoadedData={() => setLoaded(true)}
              onClick={() => onOpen(item)}
            />
          ) : (
            <img
              src={blobUrl}
              alt={item.file_name || 'photo'}
              onLoad={() => setLoaded(true)}
              onClick={() => onOpen(item)}
              className={`h-full w-full cursor-zoom-in object-cover transition-opacity duration-300 ${loaded ? 'opacity-100' : 'opacity-0'}`}
            />
          )
        ) : (
          <div className="flex h-full items-center justify-center">
            <svg className="h-5 w-5 animate-spin text-slate-500" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
            </svg>
          </div>
        )
      ) : (
        <div className="flex h-full flex-col items-center justify-center gap-1 p-2 text-center">
          <svg className="h-6 w-6 flex-shrink-0 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
          </svg>
          <p className="truncate text-[11px] text-slate-400 max-w-full">{item.file_name || 'File'}</p>
        </div>
      )}

      <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent px-2 py-1.5 opacity-0 transition group-hover:opacity-100">
        <p className="truncate text-[11px] text-white">{item.sender_name} → {item.receiver_name}</p>
      </div>
    </div>
  );
}

export default function MediaGallery({ apiUrl, token, onClose }) {
  const [items, setItems] = useState([]);
  const [loadingAll, setLoadingAll] = useState(true);
  const [view, setView] = useState('all'); // 'all' | 'favorites'
  const [lightbox, setLightbox] = useState(null);

  const bearer = { Authorization: `Bearer ${token}` };
  const [blobCache, setBlobCache] = useState({});
  const onBlobReady = useCallback((id, url) => {
    setBlobCache((prev) => (prev[id] === url ? prev : { ...prev, [id]: url }));
  }, []);

  // Pulls every page in the background as soon as the gallery opens — no "load
  // more" click, and no waiting for scroll — so the full library is on screen fast.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      let cursor = null;
      while (!cancelled) {
        const cursorParam = cursor ? `&before_id=${cursor}` : '';
        const res = await fetch(`${apiUrl}/admin/media?limit=200${cursorParam}`, { headers: { Authorization: `Bearer ${token}` } });
        const data = res.ok ? await res.json() : [];
        if (cancelled) return;
        if (data.length) {
          setItems((prev) => [...prev, ...data]);
          cursor = data[data.length - 1].id;
        }
        if (data.length < 200) break;
      }
      if (!cancelled) setLoadingAll(false);
    })();
    return () => { cancelled = true; };
  }, [apiUrl, token]);

  const toggleFavorite = async (item) => {
    setItems((prev) => prev.map((i) => (i.id === item.id ? { ...i, favorited: !i.favorited } : i)));
    await fetch(`${apiUrl}/admin/media/${item.id}/favorite`, { method: 'PATCH', headers: bearer });
  };

  const visible = view === 'favorites' ? items.filter((i) => i.favorited) : items;
  const groups = groupByDate(visible);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 sm:px-4 sm:py-6">
      {lightbox && (
        <LightboxPortal
          items={visible.map((i) => ({ ...i, blobUrl: blobCache[i.id] }))}
          index={lightbox.index}
          apiUrl={apiUrl}
          token={token}
          onNavigate={(i) => setLightbox({ index: i })}
          onClose={() => setLightbox(null)}
        />
      )}

      <div className="flex h-full w-full flex-col bg-slate-800 shadow-2xl sm:max-w-5xl sm:rounded-2xl">
        {/* Header */}
        <div
          className="flex flex-shrink-0 flex-col gap-3 border-b border-slate-700 px-4 pb-3 sm:flex-row sm:items-center sm:justify-between sm:px-6 sm:pb-4"
          style={{ paddingTop: 'calc(env(safe-area-inset-top) + 0.75rem)' }}
        >
          <div className="flex items-center justify-between sm:justify-start sm:gap-4">
            <h2 className="text-lg font-semibold">Media</h2>
            <button onClick={onClose} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-700 hover:text-white sm:hidden">
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="flex flex-1 rounded-xl bg-slate-900 p-1 text-sm sm:flex-none">
            <button
              onClick={() => setView('all')}
              className={`flex-1 rounded-lg px-4 py-2 font-medium transition sm:flex-none sm:px-5
                ${view === 'all' ? 'bg-indigo-600 text-white shadow' : 'text-slate-400 hover:text-white'}`}
            >
              All · {items.length}
            </button>
            <button
              onClick={() => setView('favorites')}
              className={`flex flex-1 items-center justify-center gap-1.5 rounded-lg px-4 py-2 font-medium transition sm:flex-none sm:px-5
                ${view === 'favorites' ? 'bg-amber-500 text-black shadow' : 'text-slate-400 hover:text-white'}`}
            >
              <svg className="h-4 w-4" fill={view === 'favorites' ? 'currentColor' : 'none'} viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z" />
              </svg>
              Favorites · {items.filter((i) => i.favorited).length}
            </button>
          </div>

          <button onClick={onClose} className="hidden rounded-lg p-1.5 text-slate-400 hover:bg-slate-700 hover:text-white sm:block">
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-3 py-4 sm:px-6">
          {visible.length === 0 && !loadingAll && (
            <div className="flex h-full flex-col items-center justify-center gap-2 text-center text-sm text-slate-500">
              {view === 'favorites' ? (
                <>
                  <svg className="h-8 w-8 text-slate-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z" />
                  </svg>
                  <p>No favorites yet</p>
                  <p className="text-xs text-slate-600">Tap the star on any photo to save it here</p>
                </>
              ) : (
                'No media uploaded yet'
              )}
            </div>
          )}

          {groups.map((group) => (
            <div key={group.label} className="mb-6">
              <h3 className="sticky top-0 z-[1] mb-2 bg-slate-800/95 py-1 text-xs font-semibold uppercase tracking-wider text-slate-400 backdrop-blur">
                {group.label}
              </h3>
              <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6">
                {group.items.map((item) => (
                  <MediaTile
                    key={item.id}
                    item={item}
                    apiUrl={apiUrl}
                    token={token}
                    onToggleFavorite={toggleFavorite}
                    onOpen={(it) => setLightbox({ index: visible.findIndex((v) => v.id === it.id) })}
                    onBlobReady={onBlobReady}
                  />
                ))}
              </div>
            </div>
          ))}

          {loadingAll && (
            <div className="flex items-center justify-center gap-2 py-6 text-sm text-slate-500">
              <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
              </svg>
              Loading photos…
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
